import {
  emailProblem,
  nameProblem,
  passwordProblem,
  phoneDigits,
  phoneProblem,
} from '@quickreport/domain';
import { Button, Field, Input, LabBackdrop, PasswordInput } from '@quickreport/ui';
import { QrCode, ShieldCheck, WifiOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { describeError } from '../lib/api';
import {
  lastSignInMode,
  rememberMe,
  type SignInMode,
  setLastSignInMode,
  setRememberMe,
} from '../lib/remember';
import { useAuth } from './AuthProvider';

function Card({
  children,
  title,
  subtitle,
}: {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
}) {
  return (
    <LabBackdrop>
      <div className="w-full max-w-md">
        <div className="rounded-3xl border border-white bg-paper/95 p-6 shadow-xl shadow-accent/15 backdrop-blur-sm sm:p-8">
          <div className="mb-5 flex flex-col items-center text-center">
            <img src="/labview-mark.svg" alt="" className="h-14 w-14" />
            <span className="mt-2 text-xl font-semibold tracking-tight">LabView</span>
            <a
              href="https://aharbal.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[12px] text-slate hover:text-navy hover:underline"
            >
              by Aharbal
            </a>
          </div>
          <h1 className="text-center text-xl font-semibold">{title}</h1>
          {subtitle ? <p className="mt-1 text-center text-slate">{subtitle}</p> : null}
          <div className="mt-6 grid gap-4">{children}</div>
        </div>
        <ul className="mt-4 flex flex-wrap justify-center gap-x-5 gap-y-1 text-[12px] text-slate">
          <li className="flex items-center gap-1.5">
            <WifiOff size={13} className="text-accent" /> Works without internet
          </li>
          <li className="flex items-center gap-1.5">
            <ShieldCheck size={13} className="text-accent" /> Private to your lab
          </li>
          <li className="flex items-center gap-1.5">
            <QrCode size={13} className="text-accent" /> Reports patients can verify
          </li>
        </ul>
      </div>
    </LabBackdrop>
  );
}

function ErrorText({ message }: { message: string | null }) {
  return message ? (
    <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-[13px] text-flag">
      {message}
    </p>
  ) : null;
}

export function SignInPage({ notice }: { notice?: string }) {
  const { requestCode, verifyCode, signInWithPassword } = useAuth();
  const [mode, setMode] = useState<SignInMode>(lastSignInMode);
  const [password, setPassword] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [challenge, setChallenge] = useState<{
    id: string;
    maskedTo: string;
    devOtp?: string;
  } | null>(null);
  const [otp, setOtp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(0);
  const [remember, setRemember] = useState(rememberMe);

  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  // Said beside the field itself, as soon as the person leaves it or types too many digits.
  const [idTouched, setIdTouched] = useState(false);
  const idErr = (() => {
    const t = identifier.trim();
    if (!t) return 'Enter your email address or mobile number';
    return t.includes('@') ? emailProblem(t) : phoneProblem(t, true);
  })();
  const showIdErr = idTouched || phoneDigits(identifier).length > 10 ? idErr : null;

  async function send() {
    if (idErr) {
      setIdTouched(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await requestCode(identifier.trim());
      setChallenge({
        id: r.challengeId,
        maskedTo: r.maskedTo,
        ...(r.devOtp ? { devOtp: r.devOtp } : {}),
      });
      setOtp('');
      setWait(r.resendAfterSeconds);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  }

  async function verify(code = otp) {
    if (!challenge) return;
    setBusy(true);
    setError(null);
    try {
      await verifyCode(challenge.id, code, remember);
    } catch (err) {
      setError(describeError(err));
      setBusy(false);
    }
  }

  async function withPassword() {
    if (idErr) {
      setIdTouched(true);
      return;
    }
    if (!password) {
      setError('Enter your password');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setLastSignInMode('password');
      await signInWithPassword(identifier.trim(), password, remember);
    } catch (err) {
      setError(describeError(err));
      setBusy(false);
    }
  }

  function choose(next: SignInMode) {
    setMode(next);
    setError(null);
    setLastSignInMode(next);
  }

  if (!challenge) {
    return (
      <Card
        title="Sign in"
        subtitle={
          mode === 'code'
            ? 'Enter your email address or mobile number. We will send you a code.'
            : 'Enter your email address or mobile number and your password.'
        }
      >
        {notice ? (
          <p className="rounded-md bg-brand-soft px-3 py-2 text-[13px] text-brand">{notice}</p>
        ) : null}
        <div
          role="tablist"
          aria-label="How to sign in"
          className="grid grid-cols-2 gap-1 rounded-xl bg-canvas p-1 text-[13px] font-medium"
        >
          {(
            [
              ['code', 'Get a code'],
              ['password', 'Use password'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={mode === value}
              onClick={() => choose(value)}
              className={`h-9 rounded-lg ${mode === value ? 'bg-paper text-brand shadow-sm' : 'text-slate hover:text-ink'}`}
            >
              {label}
            </button>
          ))}
        </div>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void (mode === 'code' ? send() : withPassword());
          }}
        >
          <Field label="Email or mobile number" error={showIdErr}>
            <Input
              autoFocus
              autoComplete="username"
              inputMode="email"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              onBlur={() => identifier.trim() && setIdTouched(true)}
              placeholder="you@lab.in or 98765 43210"
              className="h-10"
            />
          </Field>
          {mode === 'password' ? (
            <Field label="Password">
              <PasswordInput
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-10"
              />
            </Field>
          ) : null}
          <label className="flex min-h-9 items-start gap-2.5 rounded-xl bg-canvas p-2.5 text-[13px]">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-brand"
              checked={remember}
              onChange={(e) => {
                setRemember(e.target.checked);
                setRememberMe(e.target.checked);
              }}
            />
            <span>
              <span className="font-medium text-ink">Remember me</span>
              <span className="block text-slate">
                Stay signed in on this computer. Untick to sign out when you close the browser.
              </span>
            </span>
          </label>
          <ErrorText message={error} />
          {mode === 'code' ? (
            <Button tone="primary" className="h-10 justify-center" disabled={busy} type="submit">
              {busy ? 'Sending…' : 'Send code'}
            </Button>
          ) : (
            <Button tone="primary" className="h-10 justify-center" disabled={busy} type="submit">
              {busy ? 'Signing in…' : 'Sign in'}
            </Button>
          )}
          {mode === 'code' ? (
            <p className="text-[12px] text-slate">
              Mobile numbers get the code on WhatsApp. Email addresses get it by email. New to
              LabView? Get a code and your account is made when you enter it.
            </p>
          ) : (
            <p className="text-[12px] text-slate">
              No password yet, or forgot it?{' '}
              <button
                type="button"
                className="text-brand hover:underline"
                onClick={() => choose('code')}
              >
                Get a code instead
              </button>
              . You can choose a password after you sign in, under Account &amp; Team.
            </p>
          )}
        </form>
      </Card>
    );
  }

  return (
    <Card title="Enter your code" subtitle={`We sent a 6-digit code to ${challenge.maskedTo}.`}>
      {challenge.devOtp ? (
        <button
          type="button"
          onClick={() => {
            setOtp(challenge.devOtp ?? '');
            void verify(challenge.devOtp);
          }}
          className="rounded-md border border-dashed border-warn bg-amber-50 px-3 py-2 text-left text-[13px] text-warn"
        >
          Development mode: the code is <b className="font-mono">{challenge.devOtp}</b>. Click to
          use it.
        </button>
      ) : null}
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void verify();
        }}
      >
        <Field label="6-digit code">
          <Input
            autoFocus
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
            className="h-11 text-center font-mono text-lg tracking-[0.4em]"
          />
        </Field>
        <ErrorText message={error} />
        <Button
          tone="primary"
          className="h-10 justify-center"
          disabled={busy || otp.length !== 6}
          type="submit"
        >
          {busy ? 'Checking…' : 'Sign in'}
        </Button>
      </form>
      <div className="flex items-center justify-between text-[13px]">
        <button
          type="button"
          className="text-brand hover:underline"
          onClick={() => setChallenge(null)}
        >
          Use a different address
        </button>
        <button
          type="button"
          className="text-brand hover:underline disabled:text-slate disabled:no-underline"
          disabled={wait > 0 || busy}
          onClick={() => void send()}
        >
          {wait > 0 ? `Send again in ${wait}s` : 'Send again'}
        </button>
      </div>
    </Card>
  );
}

const LEVELS = [
  {
    value: 'simple',
    title: 'Just me, or a small team',
    text: 'Enter results, then sign and deliver in one step.',
  },
  {
    value: 'standard',
    title: 'Technicians and a pathologist',
    text: 'Technicians enter results; the pathologist signs.',
  },
  {
    value: 'nabl',
    title: 'NABL accredited lab',
    text: 'Adds a verification step and sample receiving by department.',
  },
] as const;

export function OnboardingPage({ userName }: { userName: string }) {
  const { createLab, signOut } = useAuth();
  const [yourName, setYourName] = useState(userName);
  const [labName, setLabName] = useState('');
  const [siteName, setSiteName] = useState('');
  const [level, setLevel] = useState<'simple' | 'standard' | 'nabl'>('simple');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const labErr = nameProblem(labName, 'the name of your lab');
  const nameErr = nameProblem(yourName, 'your name');
  const passwordErr = password ? passwordProblem(password) : null;
  const [tried, setTried] = useState(false);

  async function submit() {
    if (labErr || nameErr || passwordErr) {
      setTried(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await createLab({
        yourName,
        labName,
        siteName: siteName || labName,
        workflowLevel: level,
        ...(password ? { password } : {}),
      });
    } catch (err) {
      setError(describeError(err));
      setBusy(false);
    }
  }

  return (
    <Card
      title="Set up your lab"
      subtitle="This takes a minute. You can change everything later in Report Settings."
    >
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field label="Your name" error={tried ? nameErr : null}>
          <Input
            value={yourName}
            onChange={(e) => setYourName(e.target.value)}
            placeholder="Dr. PQR"
          />
        </Field>
        <Field label="Lab name" error={tried ? labErr : null}>
          <Input
            autoFocus
            value={labName}
            onChange={(e) => setLabName(e.target.value)}
            placeholder="ABC Diagnostics"
          />
        </Field>
        <div className="grid gap-3">
          <Field label="This lab's location or name" hint="Shown to your staff. Optional.">
            <Input
              value={siteName}
              onChange={(e) => setSiteName(e.target.value)}
              placeholder="Sample City"
            />
          </Field>
        </div>
        <Field
          label="Password (optional)"
          hint="So you can sign in without waiting for a code. You can also set it later."
          error={tried ? passwordErr : null}
        >
          <PasswordInput
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <fieldset className="grid gap-2">
          <legend className="mb-1 text-[12px] font-medium text-slate">
            How does your lab work?
          </legend>
          {LEVELS.map((l) => (
            <label
              key={l.value}
              className={`flex cursor-pointer gap-3 rounded-lg border p-3 ${level === l.value ? 'border-brand bg-brand-soft' : 'border-line hover:bg-canvas'}`}
            >
              <input
                type="radio"
                name="level"
                checked={level === l.value}
                onChange={() => setLevel(l.value)}
                className="mt-1 accent-brand"
              />
              <span>
                <span className="block font-medium">{l.title}</span>
                <span className="block text-[13px] text-slate">{l.text}</span>
              </span>
            </label>
          ))}
        </fieldset>
        <ErrorText message={error} />
        <Button tone="primary" className="h-10 justify-center" disabled={busy} type="submit">
          {busy ? 'Creating…' : 'Create my lab'}
        </Button>
      </form>
      <button
        type="button"
        className="text-left text-[13px] text-slate hover:text-ink"
        onClick={() => void signOut(true)}
      >
        Sign out
      </button>
    </Card>
  );
}

export function NeedInternet() {
  const { retry } = useAuth();
  return (
    <Card
      title="Connect to the internet"
      subtitle="The first time you use LabView on this computer, it needs to download your lab's settings."
    >
      <p className="text-slate">After that, everything keeps working without internet.</p>
      <Button tone="primary" className="h-10 justify-center" onClick={retry}>
        Try again
      </Button>
    </Card>
  );
}

export function SwitchUser({ pending }: { pending: number }) {
  const { wipeAndRestart } = useAuth();
  const [sure, setSure] = useState(false);
  return (
    <Card
      title="This computer has someone else's data"
      subtitle="It was set up by a different user. To sign in as you, its data must be cleared first."
    >
      {pending > 0 ? (
        <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-[13px] text-flag">
          {pending} change{pending === 1 ? '' : 's'} on this computer{' '}
          {pending === 1 ? 'has' : 'have'} not been uploaded and will be lost. Ask the other user to
          sign in and sync first if that matters.
        </p>
      ) : null}
      {sure ? (
        <Button
          tone="primary"
          className="h-10 justify-center bg-flag hover:bg-flag"
          onClick={() => void wipeAndRestart()}
        >
          Yes, clear this computer
        </Button>
      ) : (
        <Button tone="primary" className="h-10 justify-center" onClick={() => setSure(true)}>
          Clear this computer and sign in
        </Button>
      )}
    </Card>
  );
}

export function Preparing({ message }: { message: string }) {
  return (
    <div className="grid h-full place-items-center bg-canvas text-slate" role="status">
      {message}
    </div>
  );
}

export function Fatal({ message }: { message: string }) {
  const { retry } = useAuth();
  return (
    <Card title="Something went wrong" subtitle="LabView could not start.">
      <ErrorText message={message} />
      <p className="text-[13px] text-slate">
        Close other LabView tabs and try again. Your data on this computer has not been changed.
      </p>
      <Button tone="primary" className="h-10 justify-center" onClick={retry}>
        Try again
      </Button>
    </Card>
  );
}

/** Opens the saved lab with the PIN. Works with no internet. */
export function LockedScreen({
  name,
  contact,
  labName,
  expired,
}: {
  name: string;
  contact: string;
  labName: string;
  expired: boolean;
}) {
  const { unlock, reauthenticate } = useAuth();
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(0);

  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  async function submit() {
    if (busy || wait > 0) return;
    setBusy(true);
    setError(null);
    try {
      const r = await unlock(pin);
      if (!r.ok) {
        setPin('');
        if (r.reason === 'wait') {
          setWait(r.seconds);
          setError('Too many wrong PINs. Please wait.');
        } else {
          setError(
            `That PIN is not right. ${r.triesLeft} ${r.triesLeft === 1 ? 'try' : 'tries'} left before a wait.`,
          );
        }
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      title={`Welcome back, ${name.split(' ')[0] || 'there'}`}
      subtitle={`${labName} · ${contact}`}
    >
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Field label="Your PIN">
          <Input
            autoFocus
            type="password"
            inputMode="numeric"
            autoComplete="off"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 12))}
            className="h-11 text-center font-mono text-lg tracking-[0.4em]"
          />
        </Field>
        <ErrorText message={error} />
        <Button
          tone="primary"
          className="h-10 justify-center"
          disabled={busy || wait > 0 || pin.length < 4}
          type="submit"
        >
          {wait > 0 ? `Wait ${wait}s` : busy ? 'Opening…' : 'Open LabView'}
        </Button>
      </form>
      <p className="text-[12px] text-slate">
        {expired
          ? 'Your sign-in has ended, so changes will wait to sync until you sign in again.'
          : 'This works with or without internet.'}
      </p>
      <button
        type="button"
        className="text-left text-[13px] text-brand hover:underline"
        onClick={() => reauthenticate({ forgotPin: true })}
      >
        Forgot the PIN? Sign in with a code instead
      </button>
    </Card>
  );
}

/** The LabView team paused this person or their lab. Say why, and how to get help. */
export function BlockedScreen({
  blocked,
}: {
  blocked: { kind: 'account' | 'lab'; message: string };
}) {
  const { signOut, retry } = useAuth();
  const [unsent, setUnsent] = useState<number | null>(null);
  return (
    <Card
      title={blocked.kind === 'lab' ? 'This lab is paused' : 'Your access is paused'}
      subtitle="You cannot open LabView right now."
    >
      <p role="alert" className="rounded-md bg-amber-50 px-3 py-2 text-[13px] text-warn">
        {blocked.message}
      </p>
      <p className="text-[13px] text-slate">
        Your lab’s data is safe and nothing was deleted. Once access is restored, open LabView again
        and everything will be here.
      </p>
      {unsent !== null ? (
        <p className="rounded-md bg-red-50 px-3 py-2 text-[13px] text-flag">
          {unsent} change{unsent === 1 ? '' : 's'} on this computer {unsent === 1 ? 'has' : 'have'}{' '}
          not been uploaded. If you sign out now {unsent === 1 ? 'it' : 'they'} will be lost.
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button tone="primary" onClick={retry}>
          Check again
        </Button>
        {unsent !== null ? (
          <Button tone="danger" onClick={() => void signOut(true)}>
            Sign out and lose them
          </Button>
        ) : (
          <Button
            onClick={async () => {
              const r = await signOut(false);
              if (r) setUnsent(r.blocked);
            }}
          >
            Sign out
          </Button>
        )}
      </div>
    </Card>
  );
}
